import type { StockCountItem } from '@/data/stockService';

/** Counted quantities by stock row id; a row not in the map was not counted. */
export type CountedMap = Record<number, number>;

export interface CountRow {
  id: number;
  quantity: number;
}

const EPSILON = 1e-9;

/** Counted − system; null while the row is not counted. */
export const countDifference = (
  row: CountRow,
  counted: CountedMap
): number | null =>
  counted[row.id] === undefined ? null : counted[row.id] - row.quantity;

export const hasDifference = (row: CountRow, counted: CountedMap): boolean => {
  const difference = countDifference(row, counted);
  return difference !== null && Math.abs(difference) > EPSILON;
};

/**
 * Items to send: every counted row with the system quantity the user saw
 * when counting it (`expected`), so stock that moved since — even across a
 * reload of a saved draft — comes back as drift instead of being overwritten.
 */
export function buildCountItems(
  rows: CountRow[],
  counted: CountedMap,
  expected: CountedMap = {}
): StockCountItem[] {
  return rows
    .filter((row) => counted[row.id] !== undefined)
    .map((row) => ({
      stockId: row.id,
      countedQuantity: counted[row.id],
      expectedQuantity: expected[row.id] ?? row.quantity
    }));
}

/** Rows with the quantities the server reported as changed during the count. */
export function applyDrift<T extends CountRow>(
  rows: T[],
  drift: { stockId: number; currentQuantity: number }[]
): T[] {
  const current = new Map(drift.map((d) => [d.stockId, d.currentQuantity]));
  return rows.map((row) =>
    current.has(row.id) ? { ...row, quantity: current.get(row.id)! } : row
  );
}

const draftKey = (branchId: number) => `stockCount:draft:${branchId}`;

/** A draft older than this is dropped: the shelf has changed since. */
export const COUNT_DRAFT_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

export interface CountDraft {
  counted: CountedMap;
  /** System quantity each row had when it was counted. */
  expected: CountedMap;
}

const toMap = (value: unknown, allowNegative: boolean): CountedMap =>
  value && typeof value === 'object'
    ? Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .map(([id, quantity]) => [Number(id), Number(quantity)] as const)
          .filter(
            ([id, quantity]) =>
              Number.isInteger(id) &&
              Number.isFinite(quantity) &&
              (allowNegative || quantity >= 0)
          )
      )
    : {};

/** Unsaved counts survive a refresh or a lost connection, per branch. */
export function loadCountDraft(
  branchId: number,
  now: number = Date.now()
): CountDraft {
  const empty = { counted: {}, expected: {} };
  try {
    const raw = localStorage.getItem(draftKey(branchId));
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as {
      v?: number;
      savedAt?: number;
      counted?: unknown;
      expected?: unknown;
    };
    // Drafts before v2 had no system quantities; they cannot be checked
    // for stock that moved since, so they are not restored.
    if (parsed?.v !== 2) return empty;
    if (!parsed.savedAt || now - parsed.savedAt > COUNT_DRAFT_MAX_AGE_MS) {
      return empty;
    }
    const counted = toMap(parsed.counted, false);
    // A system quantity can be negative (negative stock allowed).
    const expected = toMap(parsed.expected, true);
    // A count without its system quantity is not kept.
    for (const id of Object.keys(counted)) {
      if (expected[Number(id)] === undefined) delete counted[Number(id)];
    }
    return { counted, expected };
  } catch {
    return empty;
  }
}

export function saveCountDraft(
  branchId: number,
  draft: CountDraft,
  now: number = Date.now()
): void {
  try {
    if (Object.keys(draft.counted).length === 0) {
      localStorage.removeItem(draftKey(branchId));
    } else {
      localStorage.setItem(
        draftKey(branchId),
        JSON.stringify({ v: 2, savedAt: now, ...draft })
      );
    }
  } catch {
    // Storage off: the count still works, only the draft is not kept.
  }
}
